import React, { useCallback, useState } from 'react';
import { Page } from 'react-pdf/dist/esm/entry.webpack5';
import { useInView } from 'react-intersection-observer';

type PageProps = React.ComponentProps<typeof Page>;

type AppProps = {
  pageNumber: number;
  width: number;
  /** Extra vertical margin (px) before/after viewport to start rendering. */
  rootMargin?: string;
  onRenderSuccess?: PageProps['onRenderSuccess'];
  renderTextLayer?: boolean;
  renderAnnotationLayer?: boolean;
  className?: string;
  loading?: PageProps['loading'];
};

/** Approximate A4 height for a given render width — used as a placeholder. */
export const estimatePdfPageHeight = (width: number): number =>
  Math.round(width * 1.414);

/**
 * Renders a react-pdf `Page` only while it is near the viewport.
 * Unmounting off-screen canvases avoids blank pages on large PDFs caused by
 * GPU/canvas memory exhaustion when every page is painted at once.
 */
const LazyPdfPage: React.FC<AppProps> = ({
  pageNumber,
  width,
  rootMargin = '800px 0px',
  onRenderSuccess,
  renderTextLayer = false,
  renderAnnotationLayer = false,
  className,
  loading,
}) => {
  const [pageHeight, setPageHeight] = useState(() =>
    estimatePdfPageHeight(width)
  );
  const { ref, inView } = useInView({
    rootMargin,
    triggerOnce: false,
  });

  const handleRenderSuccess = useCallback(
    (page: any) => {
      const viewport = page?.getViewport?.({ scale: 1 });
      if (viewport?.height && viewport?.width) {
        setPageHeight(Math.round((viewport.height / viewport.width) * width));
      }
      onRenderSuccess?.(page);
    },
    [onRenderSuccess, width]
  );

  return (
    <div
      ref={ref}
      className={className}
      style={{ width, minHeight: pageHeight }}
      data-testid="lazy-pdf-page"
      data-page-number={pageNumber}
      data-in-view={inView ? 'true' : 'false'}
    >
      {inView ? (
        <Page
          pageNumber={pageNumber}
          width={width}
          renderTextLayer={renderTextLayer}
          renderAnnotationLayer={renderAnnotationLayer}
          onRenderSuccess={handleRenderSuccess}
          loading={
            loading ?? (
              <div
                className="bg-white animate-pulse"
                style={{ width, height: pageHeight }}
              />
            )
          }
        />
      ) : (
        <div
          className="bg-white"
          style={{ width, height: pageHeight }}
          aria-hidden
        />
      )}
    </div>
  );
};

export default LazyPdfPage;
